import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageMetadata } from '../data/pageMetadata';

// The build writes these tags into each page's HTML (scripts/prerender.mjs).
// On client-side navigation we update them in place rather than adding copies.
function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function setAlternates(alternates) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  for (const { hreflang, href } of alternates) {
    const el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = hreflang;
    el.href = href;
    document.head.appendChild(el);
  }
}

export default function PageMetadata() {
  const { pathname } = useLocation();
  const meta = useMemo(() => getPageMetadata(pathname), [pathname]);

  useEffect(() => {
    document.documentElement.lang = meta.lang;
    document.title = meta.title;
    setMeta('name', 'description', meta.description);
    setMeta('name', 'robots', meta.noindex ? 'noindex' : null);
    setCanonical(meta.canonical);
    setAlternates(meta.alternates);
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', meta.canonical);
    setMeta('property', 'og:image', meta.image);
    setMeta('property', 'og:image:width', meta.imageWidth);
    setMeta('property', 'og:image:height', meta.imageHeight);
    setMeta('property', 'og:image:type', meta.imageType);
    setMeta('property', 'og:image:alt', meta.imageAlt);
    setMeta('property', 'og:type', meta.post ? 'article' : 'website');
    setMeta('property', 'og:site_name', 'Cursivefont');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    setMeta('name', 'twitter:image', meta.image);
    setMeta('name', 'twitter:image:alt', meta.imageAlt);
    let schema = document.getElementById('page-schema');
    if (!meta.structuredData) schema?.remove();
    else {
      if (!schema) { schema = document.createElement('script'); schema.id = 'page-schema'; schema.type = 'application/ld+json'; document.head.appendChild(schema); }
      schema.textContent = JSON.stringify(meta.structuredData);
    }
  }, [meta]);

  return null;
}
